import { useState, useCallback, useEffect, useRef } from 'react';

export interface UseAsyncOperationOptions<T> {
  /**
   * Callback to execute when the operation succeeds
   */
  onSuccess?: (data: T) => void;
  /**
   * Callback to execute when the operation fails
   */
  onError?: (error: Error) => void;
  /**
   * Whether to execute the operation immediately on mount
   * @default false
   */
  immediate?: boolean;
  /**
   * Whether to reset state when dependencies change
   * @default true
   */
  resetOnDepsChange?: boolean;
}

export interface AsyncOperationState<T> {
  /**
   * Whether the operation is currently loading
   */
  loading: boolean;
  /**
   * The data returned from the operation
   */
  data: T | null;
  /**
   * Any error that occurred during the operation
   */
  error: Error | null;
  /**
   * Whether the operation has been called at least once
   */
  called: boolean;
}

export interface UseAsyncOperationReturn<T> extends AsyncOperationState<T> {
  /**
   * Execute the async operation
   */
  execute: () => Promise<T | undefined>;
  /**
   * Reset the state to initial values
   */
  reset: () => void;
}

/**
 * Hook for managing async operations with loading, error, and data states
 * Based on feedback from @orchard9ai/create-vite-vike-tauri generator team
 *
 * @example
 * ```tsx
 * const { loading, data, error, execute } = useAsyncOperation(
 *   async () => {
 *     const response = await fetch('/api/data');
 *     return response.json();
 *   },
 *   {
 *     onSuccess: (data) => console.log('Data loaded:', data),
 *     onError: (error) => console.error('Error:', error),
 *   }
 * );
 * ```
 */
export function useAsyncOperation<T>(
  asyncFn: () => Promise<T>,
  options: UseAsyncOperationOptions<T> = {}
): UseAsyncOperationReturn<T> {
  const { onSuccess, onError, immediate = false, resetOnDepsChange = true } = options;

  const [state, setState] = useState<AsyncOperationState<T>>({
    loading: false,
    data: null,
    error: null,
    called: false,
  });

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // Track the current operation to handle race conditions
  const operationIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const execute = useCallback(async (): Promise<T | undefined> => {
    // Increment operation ID to handle race conditions
    const currentOperationId = ++operationIdRef.current;

    if (!isMountedRef.current) return;

    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
      called: true,
    }));

    try {
      const data = await asyncFn();

      // Only update state if this is still the current operation and component is mounted
      if (currentOperationId === operationIdRef.current && isMountedRef.current) {
        setState({
          loading: false,
          data,
          error: null,
          called: true,
        });
        onSuccess?.(data);
      }

      return data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));

      // Only update state if this is still the current operation and component is mounted
      if (currentOperationId === operationIdRef.current && isMountedRef.current) {
        setState({
          loading: false,
          data: null,
          error: err,
          called: true,
        });
        onError?.(err);
      }

      throw error;
    }
  }, [asyncFn, onSuccess, onError]);

  const reset = useCallback(() => {
    setState({
      loading: false,
      data: null,
      error: null,
      called: false,
    });
    operationIdRef.current = 0;
  }, []);

  // Handle immediate execution
  useEffect(() => {
    if (immediate && !state.called) {
      execute();
    }
  }, [immediate, execute, state.called]);

  // Reset state when dependencies change (if enabled)
  useEffect(() => {
    if (resetOnDepsChange && state.called) {
      reset();
    }
  }, [asyncFn]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    ...state,
    execute,
    reset,
  };
}

/**
 * Type helper for async functions
 */
export type AsyncFunction<T> = () => Promise<T>;
